// The sheet behind a person row (docs/design/social-plan.md 3, "PersonSheet"): who they are, how
// you stand to each other in words, what you have in common, and everything you can do about it
// (add, answer, unfriend, block, report). The edge shows only what the viewer may see; a person who
// cannot be shown is one calm line, whatever the reason, so the sheet never hints why.
import type { CardView, PersonProfile, PublicUser, ReactionKind } from "@proef/contract";
import { useCallback, useEffect, useState } from "react";
import { ActivityCard } from "./ActivityCard";
import { useConfirm } from "./ConfirmSheet";
import { JoinTastingButton } from "./JoinTastingButton";
import { initialsOf } from "./PersonRow";
import { ReportSheet } from "./ReportSheet";
import { UserCard } from "./UserCard";
import { EdgeApiError } from "../lib/edgeApi";
import { mk, useT } from "../lib/i18n";
import { navigate } from "../lib/router";
import {
  acceptFriendRequest,
  blockUser,
  cancelFriendRequest,
  declineFriendRequest,
  sendFriendRequest,
  unblockUser,
  unfriend,
} from "../lib/social/friendsApi";
import { friendRequestMessage, friendRequestRefusal } from "../lib/social/friendRequestRefusal";
import { deleteReaction, listUserCards, putReaction } from "../lib/social/cardsApi";
import { presenceView } from "../lib/social/lastActive";
import { getPersonProfile } from "../lib/social/peopleApi";
import { usePresence } from "../lib/social/presenceList";
import {
  Avatar,
  Button,
  Caption,
  Column,
  EmptyState,
  ErrorBanner,
  Heading,
  ListRow,
  Row,
  Section,
  Sheet,
  Skeleton,
  Tag,
} from "../ui";

export interface PersonSheetProps {
  userId: string;
  /** What the opening row already knows, shown while the profile loads. */
  hint?: PublicUser | undefined;
  onClose: () => void;
  /** Called after anything that changed how the two stand to each other, so the list behind refreshes. */
  onChanged?: (() => void) | undefined;
}

type Load =
  | { status: "loading" }
  | { status: "ready"; profile: PersonProfile }
  | { status: "unavailable" }
  | { status: "error" };

export function PersonSheet({
  userId,
  hint,
  onClose,
  onChanged,
}: PersonSheetProps): React.JSX.Element {
  const { t, locale } = useT();
  // Whoever opened this sheet already loaded who is open; reading it again would be a second fetch.
  const presence = presenceView(usePresence().get(userId), Date.now(), locale, t);
  const { ask, confirmation } = useConfirm();
  const [load, setLoad] = useState<Load>({ status: "loading" });
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | undefined>(undefined);
  const [reporting, setReporting] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const fetchProfile = useCallback(async (): Promise<Load> => {
    try {
      return { status: "ready", profile: await getPersonProfile(userId) };
    } catch (err) {
      return err instanceof EdgeApiError && err.status === 404
        ? { status: "unavailable" }
        : { status: "error" };
    }
  }, [userId]);

  useEffect(() => {
    let cancelled = false;
    setLoad({ status: "loading" });
    fetchProfile()
      .then((loaded) => {
        if (!cancelled) setLoad(loaded);
        return undefined;
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // `attempt` is the retry button: the same read, run again.
  }, [fetchProfile, attempt]);

  // The cards the edge lets this viewer see (deferred from the cards work): none for someone whose
  // cards are not shared with them, which reads the same as someone with no cards.
  const [cards, setCards] = useState<readonly CardView[]>([]);
  const [reacting, setReacting] = useState<ReadonlySet<string>>(new Set());
  const ready = load.status === "ready";
  useEffect(() => {
    if (!ready) return undefined;
    let cancelled = false;
    listUserCards(userId)
      .then((list) => {
        if (!cancelled) setCards(list.items);
        return undefined;
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [ready, userId]);

  const react = (cardId: string, kind: ReactionKind | null): void => {
    setReacting((held) => new Set(held).add(cardId));
    (kind === null ? deleteReaction(cardId) : putReaction(cardId, kind))
      .then((updated) => {
        setCards((held) => held.map((card) => (card.id === cardId ? updated : card)));
        return undefined;
      })
      .catch(() => undefined)
      .finally(() => {
        setReacting((held) => {
          const next = new Set(held);
          next.delete(cardId);
          return next;
        });
      });
  };

  /** Runs one change, then shows the person again as they now stand. */
  const change = async (work: () => Promise<unknown>): Promise<void> => {
    setBusy(true);
    setActionError(undefined);
    try {
      await work();
      setLoad(await fetchProfile());
      onChanged?.();
    } catch (error) {
      const refusal = friendRequestRefusal(error);
      setActionError(
        refusal === undefined
          ? t("common.error.generic")
          : friendRequestMessage(refusal, t, locale),
      );
    } finally {
      setBusy(false);
    }
  };

  const shown =
    load.status === "ready" ? load.profile.user : load.status === "unavailable" ? undefined : hint;
  const name = shown?.displayName;
  const label = name === undefined ? t("social.profile.title") : t("people.sheet.label", { name });

  const actions = (profile: PersonProfile): React.ReactNode => {
    const { state, friendshipId } = profile.relationship;
    const person = profile.user.displayName;
    const answer = (
      tone: "primary" | "secondary",
      key: "add" | "cancel" | "accept" | "decline",
      run: () => Promise<unknown>,
    ): React.ReactNode => (
      <Button
        full
        tone={tone}
        label={t(mk(`people.action.${key}`))}
        disabled={busy}
        data-testid={`person-${key}`}
        onPress={() => {
          void change(run);
        }}
      />
    );
    return (
      <Column gap="2">
        {state === "none" && answer("primary", "add", () => sendFriendRequest(userId))}
        {state === "request_sent" &&
          friendshipId !== null &&
          answer("secondary", "cancel", () => cancelFriendRequest(friendshipId))}
        {state === "request_received" && friendshipId !== null && (
          <>
            {answer("primary", "accept", () => acceptFriendRequest(friendshipId))}
            {answer("secondary", "decline", () => declineFriendRequest(friendshipId))}
          </>
        )}
        {state === "friends" && friendshipId !== null && (
          <Button
            full
            tone="secondary"
            label={t("people.action.unfriend")}
            disabled={busy}
            data-testid="person-unfriend"
            onPress={() => {
              ask({
                title: t("people.confirm.unfriend.title", { name: person }),
                body: t("people.confirm.unfriend.body"),
                confirmLabel: t("people.confirm.unfriend.yes"),
                onConfirm: () => change(() => unfriend(friendshipId)),
              });
            }}
          />
        )}
        {state === "blocked" ? (
          <Button
            full
            tone="secondary"
            label={t("people.action.unblock")}
            disabled={busy}
            data-testid="person-unblock"
            onPress={() => {
              void change(() => unblockUser(userId));
            }}
          />
        ) : (
          <Button
            full
            tone="ghost"
            label={t("people.action.block")}
            disabled={busy}
            data-testid="person-block"
            onPress={() => {
              ask({
                title: t("people.confirm.block.title", { name: person }),
                body: t("people.confirm.block.body", { name: person }),
                confirmLabel: t("people.confirm.block.yes"),
                onConfirm: () => change(() => blockUser(userId)),
              });
            }}
          />
        )}
        <Button
          full
          tone="ghost"
          label={t("people.action.report")}
          data-testid="person-report"
          onPress={() => {
            setReporting(true);
          }}
        />
      </Column>
    );
  };

  const shared = (profile: PersonProfile): React.ReactNode => {
    const moreFriends = profile.mutualFriendCount - profile.mutualFriends.length;
    if (profile.mutualFriendCount === 0 && profile.mutualGroupCount === 0) {
      return <Caption>{t("people.mutual.none")}</Caption>;
    }
    return (
      <Column gap="4">
        {profile.mutualFriendCount > 0 && (
          <Section title={t("people.mutual.friends")} level={3} data-testid="person-mutual-friends">
            <Column>
              {profile.mutualFriends.map((friend) => (
                <UserCard
                  key={friend.id}
                  title={friend.displayName}
                  initials={initialsOf(friend.displayName)}
                  avatarUrl={friend.avatarUrl ?? undefined}
                />
              ))}
              {moreFriends > 0 && <Caption>{t("people.mutual.more", { n: moreFriends })}</Caption>}
            </Column>
          </Section>
        )}
        {profile.mutualGroupCount > 0 && (
          <Section title={t("people.mutual.groups")} level={3} data-testid="person-mutual-groups">
            <Column>
              {profile.mutualGroups.map((group) => (
                <ListRow
                  key={group.id}
                  title={group.name}
                  leading={
                    <Avatar initials={initialsOf(group.name)} src={group.avatarUrl ?? undefined} />
                  }
                  onPress={() => {
                    onClose();
                    navigate(`/groups/${encodeURIComponent(group.id)}`);
                  }}
                />
              ))}
              {profile.mutualGroupCount > profile.mutualGroups.length && (
                <Caption>
                  {t("people.mutual.more", {
                    n: profile.mutualGroupCount - profile.mutualGroups.length,
                  })}
                </Caption>
              )}
            </Column>
          </Section>
        )}
      </Column>
    );
  };

  return (
    <>
      <Sheet label={label} onClose={onClose} testId="person-sheet">
        {shown !== undefined && (
          <Row gap="3">
            <Avatar
              initials={initialsOf(shown.displayName)}
              src={shown.avatarUrl ?? undefined}
              size="detail"
              presence={presence.avatar}
            />
            <Column flex={1} minWidth={0} gap="1">
              <Heading render="h2">{shown.displayName}</Heading>
              {shown.handle !== null && <Caption>@{shown.handle}</Caption>}
              {presence.line !== undefined && (
                <Caption data-testid="person-presence">{presence.line}</Caption>
              )}
              {presence.join !== undefined && <JoinTastingButton join={presence.join} />}
            </Column>
          </Row>
        )}
        {load.status === "loading" && (
          <Column gap="3" aria-busy={true}>
            <Skeleton kind="row" />
            <Skeleton kind="row" />
          </Column>
        )}
        {load.status === "unavailable" && (
          <EmptyState icon="person" title={t("people.sheet.unavailable")} />
        )}
        {load.status === "error" && (
          <Column gap="3">
            <ErrorBanner message={t("common.error.generic")} />
            <Button
              tone="secondary"
              onPress={() => {
                setAttempt((n) => n + 1);
              }}
            >
              {t("common.action.retry")}
            </Button>
          </Column>
        )}
        {load.status === "ready" && (
          <Column gap="4">
            {load.profile.relationship.state !== "none" && (
              <Row>
                <Tag
                  solid={load.profile.relationship.state === "friends"}
                  label={t(mk(`people.state.${load.profile.relationship.state}`))}
                  data-testid="person-state"
                />
              </Row>
            )}
            {shared(load.profile)}
            {cards.length > 0 && (
              <Section title={t("social.person.cards")} level={3} data-testid="person-cards">
                <Column gap="3">
                  {cards.map((card) => (
                    <ActivityCard
                      key={card.id}
                      testId={`person-card-${card.id}`}
                      card={card}
                      reacting={reacting.has(card.id)}
                      onReact={(kind) => {
                        react(card.id, kind);
                      }}
                    />
                  ))}
                </Column>
              </Section>
            )}
            {actionError !== undefined && <ErrorBanner message={actionError} />}
            {actions(load.profile)}
          </Column>
        )}
      </Sheet>
      {reporting && shown !== undefined && (
        <ReportSheet
          subject={{ kind: "user", id: userId, name: shown.displayName }}
          onClose={() => {
            setReporting(false);
          }}
        />
      )}
      {confirmation}
    </>
  );
}
